import { FC, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import type { BadgeTemplate, BadgeSide, BadgeElement, BadgeElementType } from "../types";
import { loadTemplates, loadTemplatesFromLocalStorage, parseTemplateData } from "./BadgeTemplatesPage";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const CANVAS_DISPLAY_W = 270;

const PRESET_SIZES = [
    { label: 'Lanyard Portrait',    width: 630,  height: 1000, note: '5.3×8.5cm' },
    { label: 'Badge Grande',        width: 740,  height: 1050, note: '6.3×8.9cm' },
    { label: 'Credit Card',         width: 1012, height: 638,  note: '8.6×5.4cm' },
    { label: 'Quadrato',            width: 700,  height: 700,  note: '5.9×5.9cm' },
];

const GRID_OPTIONS = [
    { label: '0.1 cm', value: 0.1 },
    { label: '0.25 cm', value: 0.25 },
    { label: '0.5 cm', value: 0.5 },
    { label: '1 cm', value: 1 },
    { label: '2 cm', value: 2 },
    { label: '5 cm', value: 5 },
];

const ELEMENT_LABELS: Record<BadgeElementType, string> = {
    name: 'Nome Persona', qrcode: 'QR Code', number: 'Numero Badge',
    area: 'Area Accesso', location: 'Città/Location', custom_text: 'Testo Fisso',
    box: 'Box / Rettangolo', image: 'Immagine',
};

const ELEMENT_DOT: Record<BadgeElementType, string> = {
    name: 'bg-sky-500', qrcode: 'bg-emerald-500', number: 'bg-violet-500',
    area: 'bg-amber-500', location: 'bg-orange-500', custom_text: 'bg-slate-500',
    box: 'bg-pink-500', image: 'bg-teal-500',
};

const defaultEl = (type: BadgeElementType, bW: number, bH: number): Omit<BadgeElement, 'id'> => {
    const b = { fontSize: 24, fontStyle: 'normal' as const, color: '#ffffff', align: 'center' as const };
    switch (type) {
        case 'name':        return { ...b, type, x: 20, y: Math.round(bH * .38), width: bW - 40, height: 90,  fontSize: 72, fontStyle: 'bold' };
        case 'qrcode':      return { ...b, type, x: Math.round(bW * .2), y: Math.round(bH * .45), width: Math.round(bW * .6), height: Math.round(bW * .6) };
        case 'number':      return { ...b, type, x: Math.round(bW * .75), y: 16, width: Math.round(bW * .22), height: 60, fontSize: 44, fontStyle: 'bold', align: 'right' };
        case 'area':        return { ...b, type, x: 20, y: Math.round(bH * .88), width: Math.round(bW * .65), height: 60, fontSize: 44, fontStyle: 'bold', align: 'left' };
        case 'location':    return { ...b, type, x: Math.round(bW * .68), y: Math.round(bH * .88), width: Math.round(bW * .3), height: 60, fontSize: 36, fontStyle: 'bold', color: '#000000' };
        case 'custom_text': return { ...b, type, x: 20, y: Math.round(bH * .75), width: bW - 40, height: 160, fontSize: 22, text: 'Inserisci testo...' };
        case 'box':         return { ...b, type, x: 20, y: Math.round(bH * .85), width: bW - 40, height: 80, bgColor: '#0284c7', borderRadius: 0, borderColor: '#ffffff', borderWidth: 0 };
        case 'image':       return { ...b, type, x: 0, y: 0, width: bW, height: bH };
    }
};

const emptyTemplate = (): BadgeTemplate => ({
    id: 0, name: 'Nuovo Template', width: 630, height: 1000, dpi: 300, createdAt: new Date().toISOString(),
    front: { bgColor: '#1e293b', elements: [] },
    back:  { bgColor: '#1e293b', elements: [] },
});

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';
interface DragState   { id: string; startX: number; startY: number; origX: number; origY: number }
interface ResizeState { id: string; handle: ResizeHandle; startX: number; startY: number; origX: number; origY: number; origW: number; origH: number }

const BadgeDesignerPage: FC = () => {
    const { id }       = useParams<{ id?: string }>();
    const navigate     = useNavigate();
    const location     = useLocation();
    const { user }     = useAuth();
    const base = location.pathname.startsWith('/admin') ? '/admin' : '/client';
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'ROLE_ADMIN';

    const [template,    setTemplate]    = useState<BadgeTemplate>(emptyTemplate());
    const [activeSide,  setActiveSide]  = useState<'front' | 'back'>('front');
    const [selectedId,  setSelectedId]  = useState<string | null>(null);
    const [dragging,    setDragging]    = useState<DragState | null>(null);
    const [resizing,    setResizing]    = useState<ResizeState | null>(null);
    const [showSizeModal, setShowSizeModal] = useState(false);
    const [saved,       setSaved]       = useState(false);
    const [clientId,    setClientId]    = useState<number | ''>('');

    // Unit + grid
    const [unit,        setUnit]        = useState<'px' | 'cm'>('px');
    const [showGrid,    setShowGrid]    = useState(false);
    const [gridSnap,    setGridSnap]    = useState(true);
    const [gridSizeCm,  setGridSizeCm]  = useState(1);

    const activeSideRef = useRef(activeSide);
    activeSideRef.current = activeSide;

    const imageInputRef       = useRef<HTMLInputElement>(null);
    const pendingImageIdRef   = useRef<string | null>(null);

    /* ── load ── */
    useEffect(() => {
        if (id) {
            loadTemplates().then(templates => {
                const tpl = templates.find(t => t.id === Number(id));
                if (tpl) setTemplate(tpl); else navigate(`${base}/badges`);
            });
        }
    }, [id, navigate, base]);

    /* ── derived ── */
    const DPI       = template.dpi || 300;
    const PX_PER_CM = DPI / 2.54;
    const scale     = CANVAS_DISPLAY_W / template.width;
    const canvasH   = Math.round(template.height * scale);

    const pxToCm = (px: number) => parseFloat((px / PX_PER_CM).toFixed(2));
    const cmToPx = (cm: number) => Math.round(cm * PX_PER_CM);

    const toDisplay  = (px: number) => unit === 'cm' ? pxToCm(px) : px;
    const fromDisplay = (v: number) => unit === 'cm' ? cmToPx(v)  : Math.round(v);

    const snapPx = useCallback((px: number) => {
        if (!showGrid || !gridSnap) return px;
        const g = gridSizeCm * PX_PER_CM;
        return Math.round(px / g) * g;
    }, [showGrid, gridSnap, gridSizeCm, PX_PER_CM]);

    const badgeCmW = pxToCm(template.width).toFixed(1);
    const badgeCmH = pxToCm(template.height).toFixed(1);

    const currentSide: BadgeSide = template[activeSide];

    /* ── helpers ── */
    const updateSide = useCallback((side: 'front' | 'back', patch: Partial<BadgeSide>) =>
        setTemplate(p => ({ ...p, [side]: { ...p[side], ...patch } })), []);

    const updateEl = useCallback((elId: string, patch: Partial<BadgeElement>) =>
        setTemplate(p => {
            const s = activeSideRef.current;
            return { ...p, [s]: { ...p[s], elements: p[s].elements.map(e => e.id === elId ? { ...e, ...patch } : e) } };
        }), []);

    const deleteEl = (elId: string) => {
        const s = activeSide;
        setTemplate(p => ({ ...p, [s]: { ...p[s], elements: p[s].elements.filter(e => e.id !== elId) } }));
        if (selectedId === elId) setSelectedId(null);
    };

    const moveEl = (elId: string, dir: 'up' | 'down') => {
        const s = activeSide;
        setTemplate(p => {
            const els = [...p[s].elements];
            const i = els.findIndex(e => e.id === elId);
            if (dir === 'up'   && i < els.length - 1) [els[i], els[i + 1]] = [els[i + 1], els[i]];
            if (dir === 'down' && i > 0)               [els[i], els[i - 1]] = [els[i - 1], els[i]];
            return { ...p, [s]: { ...p[s], elements: els } };
        });
    };

    const addElement = (type: BadgeElementType) => {
        const newId = `el_${Date.now()}`;
        const el: BadgeElement = { id: newId, ...defaultEl(type, template.width, template.height) };
        const s = activeSide;
        setTemplate(p => ({ ...p, [s]: { ...p[s], elements: [...p[s].elements, el] } }));
        setSelectedId(newId);
        return newId;
    };

    const handleAddImage = () => {
        const newId = addElement('image');
        pendingImageIdRef.current = newId;
        imageInputRef.current?.click();
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !pendingImageIdRef.current) return;
        const reader = new FileReader();
        reader.onload = ev => {
            if (pendingImageIdRef.current) {
                updateEl(pendingImageIdRef.current, { imageData: ev.target?.result as string });
                pendingImageIdRef.current = null;
            }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleChangeImageForEl = (elId: string) => {
        pendingImageIdRef.current = elId;
        imageInputRef.current?.click();
    };

    /* ── drag ── */
    const handleElMouseDown = (e: React.MouseEvent, el: BadgeElement) => {
        if (resizing) return;
        e.preventDefault(); e.stopPropagation();
        setSelectedId(el.id);
        setDragging({ id: el.id, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y });
    };

    useEffect(() => {
        if (!dragging) return;
        const s = CANVAS_DISPLAY_W / template.width;
        const onMove = (e: MouseEvent) => {
            const dx = (e.clientX - dragging.startX) / s;
            const dy = (e.clientY - dragging.startY) / s;
            const side = activeSideRef.current;
            setTemplate(p => ({ ...p, [side]: { ...p[side], elements: p[side].elements.map(el =>
                el.id === dragging.id
                    ? { ...el, x: Math.max(0, snapPx(dragging.origX + dx)), y: Math.max(0, snapPx(dragging.origY + dy)) }
                    : el
            ) } }));
        };
        const onUp = () => setDragging(null);
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }, [dragging, template.width, snapPx]);

    /* ── resize ── */
    const handleResizeMouseDown = (e: React.MouseEvent, el: BadgeElement, handle: ResizeHandle) => {
        e.preventDefault(); e.stopPropagation();
        setResizing({ id: el.id, handle, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y, origW: el.width, origH: el.height });
    };

    useEffect(() => {
        if (!resizing) return;
        const s = CANVAS_DISPLAY_W / template.width;
        const { origX, origY, origW, origH, handle } = resizing;
        const onMove = (e: MouseEvent) => {
            const dx = (e.clientX - resizing.startX) / s;
            const dy = (e.clientY - resizing.startY) / s;
            let x = origX, y = origY, w = origW, h = origH;
            if (handle === 'nw') { x = snapPx(origX + dx); y = snapPx(origY + dy); w = origX + origW - x; h = origY + origH - y; }
            if (handle === 'ne') { y = snapPx(origY + dy);                          w = snapPx(origX + origW + dx) - origX; h = origY + origH - y; }
            if (handle === 'sw') { x = snapPx(origX + dx);                          w = origX + origW - x; h = snapPx(origY + origH + dy) - origY; }
            if (handle === 'se') {                                                   w = snapPx(origX + origW + dx) - origX; h = snapPx(origY + origH + dy) - origY; }
            w = Math.max(10, Math.round(w)); h = Math.max(10, Math.round(h));
            const side = activeSideRef.current;
            setTemplate(p => ({ ...p, [side]: { ...p[side], elements: p[side].elements.map(el =>
                el.id === resizing.id ? { ...el, x: Math.round(x), y: Math.round(y), width: w, height: h } : el
            ) } }));
        };
        const onUp = () => setResizing(null);
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }, [resizing, template.width, snapPx]);

    /* ── save ── */
    const handleSave = async () => {
        try {
            const payload: Record<string, unknown> = {
                name: template.name,
                width: template.width,
                height: template.height,
                dpi: template.dpi,
                templateData: JSON.stringify({ front: template.front, back: template.back }),
            };
            if (isAdmin && clientId !== '') {
                payload.clientId = clientId;
            }
            const isNewTemplate = template.id === 0;
            const endpoint = isNewTemplate ? '/api/badge-templates/save' : `/api/badge-templates/update/${template.id}`;
            const response = isNewTemplate
                ? await api.post<any>(endpoint, payload)
                : await api.put<any>(endpoint, payload);

            const savedTemplate = response.data?.data;
            if (savedTemplate) {
                const t: BadgeTemplate = {
                    id: savedTemplate.id,
                    name: savedTemplate.name,
                    width: savedTemplate.width,
                    height: savedTemplate.height,
                    dpi: savedTemplate.dpi,
                    front: parseTemplateData(savedTemplate.templateData).front,
                    back: parseTemplateData(savedTemplate.templateData).back,
                    createdAt: savedTemplate.createdAt,
                };
                setTemplate(t);
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            console.error('Save failed:', err);
        }
    };

    /* ── element render ── */
    const selectedEl = currentSide.elements.find(e => e.id === selectedId) ?? null;

    const renderHandles = (el: BadgeElement) => (
        (['nw','ne','sw','se'] as ResizeHandle[]).map(h => {
            const top  = h.includes('n') ? -4 : undefined;
            const bot  = h.includes('s') ? -4 : undefined;
            const left = h.includes('w') ? -4 : undefined;
            const right= h.includes('e') ? -4 : undefined;
            return (
                <div key={h} onMouseDown={e => handleResizeMouseDown(e, el, h)}
                    style={{ position: 'absolute', width: 8, height: 8, top, bottom: bot, left, right, backgroundColor: '#38bdf8', border: '1px solid #0369a1', borderRadius: 2, zIndex: 20, cursor: `${h}-resize` }}
                />
            );
        })
    );

    const renderEl = (el: BadgeElement) => {
        const isSel = selectedId === el.id;
        const base2: React.CSSProperties = {
            position: 'absolute', left: el.x * scale, top: el.y * scale,
            width: el.width * scale, height: el.height * scale,
            cursor: (dragging?.id === el.id || resizing?.id === el.id) ? 'grabbing' : 'grab',
            boxSizing: 'border-box', userSelect: 'none', zIndex: isSel ? 10 : 1,
            outline: isSel ? '2px dashed #38bdf8' : '1px dashed rgba(255,255,255,0.12)',
            outlineOffset: '1px',
        };
        const onDown = (e: React.MouseEvent) => handleElMouseDown(e, el);
        const onClick = (e: React.MouseEvent) => { e.stopPropagation(); setSelectedId(el.id); };

        if (el.type === 'image') return (
            <div key={el.id} style={base2} onMouseDown={onDown} onClick={onClick}>
                {el.imageData
                    ? <img src={el.imageData} alt="" style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', pointerEvents: 'none' }}/>
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)' }}>
                        <span style={{ color: '#475569', fontSize: 10 }}>Nessuna immagine</span>
                      </div>}
                {isSel && renderHandles(el)}
            </div>
        );

        if (el.type === 'box') return (
            <div key={el.id} style={{ ...base2, backgroundColor: el.bgColor || 'transparent', borderRadius: (el.borderRadius || 0) * scale, border: (el.borderWidth || 0) > 0 ? `${(el.borderWidth || 0) * scale}px solid ${el.borderColor || '#ffffff'}` : undefined }}
                onMouseDown={onDown} onClick={onClick}>
                {isSel && renderHandles(el)}
            </div>
        );

        if (el.type === 'qrcode') return (
            <div key={el.id} style={{ ...base2, backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={onDown} onClick={onClick}>
                <svg xmlns="http://www.w3.org/2000/svg" width={Math.min(60, el.width * scale - 8)} height={Math.min(60, el.height * scale - 8)} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="1.5"><rect width="6" height="6" x="3" y="3" rx="1"/><rect width="6" height="6" x="15" y="3" rx="1"/><rect width="6" height="6" x="3" y="15" rx="1"/><path d="M15 15h6v6h-6z"/></svg>
                {isSel && renderHandles(el)}
            </div>
        );

        const label = el.type === 'name' ? '{NOME}' : el.type === 'area' ? '{AREA}' : el.type === 'location' ? '{CITTÀ}' : el.type === 'number' ? '{N}' : (el.text || '{TESTO}');
        return (
            <div key={el.id} style={base2} onMouseDown={onDown} onClick={onClick}>
                <span style={{ color: el.color, fontSize: el.fontSize * scale, fontWeight: el.fontStyle.includes('bold') ? 'bold' : 'normal', fontStyle: el.fontStyle.includes('italic') ? 'italic' : 'normal', textAlign: el.align, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: 1.2, textShadow: '0 0 6px rgba(0,0,0,0.9)' }}>
                    {label}
                </span>
                {isSel && renderHandles(el)}
            </div>
        );
    };

    /* ── grid overlay ── */
    const GridOverlay = () => {
        if (!showGrid) return null;
        const gridDisplay = gridSizeCm * PX_PER_CM * scale;
        if (gridDisplay < 4) return null;
        return (
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 6 }}>
                <defs>
                    <pattern id="grid-pat" width={gridDisplay} height={gridDisplay} patternUnits="userSpaceOnUse">
                        <path d={`M ${gridDisplay} 0 L 0 0 0 ${gridDisplay}`} fill="none" stroke="rgba(255,255,255,0.13)" strokeWidth="0.5"/>
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid-pat)"/>
                {/* CM labels along top */}
                {Array.from({ length: Math.ceil(CANVAS_DISPLAY_W / gridDisplay) }, (_, i) => (
                    i > 0 && <text key={i} x={i * gridDisplay + 2} y={10} fill="rgba(255,255,255,0.25)" fontSize={7} fontFamily="monospace">{(i * gridSizeCm).toFixed(gridSizeCm < 1 ? 2 : 0)}</text>
                ))}
                {/* CM labels along left */}
                {Array.from({ length: Math.ceil(canvasH / gridDisplay) }, (_, i) => (
                    i > 0 && <text key={i} x={2} y={i * gridDisplay - 2} fill="rgba(255,255,255,0.25)" fontSize={7} fontFamily="monospace">{(i * gridSizeCm).toFixed(gridSizeCm < 1 ? 2 : 0)}</text>
                ))}
            </svg>
        );
    };

    /* ── props panel ── */
    const numField = (label: string, pxVal: number, setter: (px: number) => void) => {
        const dispVal = toDisplay(pxVal);
        const step = unit === 'cm' ? (gridSizeCm <= 0.5 ? gridSizeCm : 0.1) : 1;
        return (
            <div>
                <label className="text-xs text-slate-500 mb-1 block">{label}</label>
                <input type="number" value={dispVal} step={step}
                    onChange={e => setter(fromDisplay(parseFloat(e.target.value) || 0))}
                    className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-sky-500"/>
            </div>
        );
    };

    const colorField = (label: string, val: string, setter: (v: string) => void) => (
        <div>
            <label className="text-xs text-slate-500 mb-1 block">{label}</label>
            <div className="flex gap-2">
                <input type="color" value={val} onChange={e => setter(e.target.value)} className="w-10 h-9 rounded-lg border border-slate-700 bg-slate-800 cursor-pointer p-0.5"/>
                <input type="text"  value={val} onChange={e => setter(e.target.value)} className="flex-1 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-sky-500 font-mono"/>
            </div>
        </div>
    );

    const PropsPanel = () => {
        if (!selectedEl) return (
            <div className="flex flex-col items-center justify-center h-48 text-center px-4">
                <svg className="w-10 h-10 text-slate-700 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/></svg>
                <p className="text-slate-500 text-sm">Clicca un elemento per modificarlo</p>
            </div>
        );

        const el = selectedEl;
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${ELEMENT_DOT[el.type]}`}/>
                        <p className="text-sm font-bold text-white">{ELEMENT_LABELS[el.type]}</p>
                    </div>
                    <button onClick={() => deleteEl(el.id)} className="text-red-400 hover:text-red-300 p-1 rounded transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    </button>
                </div>

                {/* Position */}
                <div>
                    <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Posizione ({unit})</p>
                    <div className="grid grid-cols-2 gap-2">
                        {numField('X', el.x, v => updateEl(el.id, { x: v }))}
                        {numField('Y', el.y, v => updateEl(el.id, { y: v }))}
                    </div>
                </div>

                {/* Size */}
                <div>
                    <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Dimensione ({unit})</p>
                    <div className="grid grid-cols-2 gap-2">
                        {numField('Larghezza', el.width,  v => updateEl(el.id, { width:  Math.max(1, v) }))}
                        {numField('Altezza',   el.height, v => updateEl(el.id, { height: Math.max(1, v) }))}
                    </div>
                    {unit === 'cm' && (
                        <p className="text-[10px] text-slate-600 mt-1 text-right">
                            {el.width}×{el.height} px
                        </p>
                    )}
                </div>

                {/* Image */}
                {el.type === 'image' && (
                    <div>
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Immagine</p>
                        <button onClick={() => handleChangeImageForEl(el.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-600 hover:border-slate-500 text-slate-400 hover:text-white text-sm transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            {el.imageData ? 'Cambia immagine' : 'Carica immagine'}
                        </button>
                    </div>
                )}

                {/* Box */}
                {el.type === 'box' && (
                    <div className="space-y-2">
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Box</p>
                        {colorField('Colore sfondo', el.bgColor || '#0284c7', v => updateEl(el.id, { bgColor: v }))}
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Arrotondamento ({unit})</p>
                            {numField('Raggio angoli', el.borderRadius || 0, v => updateEl(el.id, { borderRadius: v }))}
                        </div>
                        {colorField('Colore bordo', el.borderColor || '#ffffff', v => updateEl(el.id, { borderColor: v }))}
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Spessore bordo ({unit})</p>
                            {numField('Spessore', el.borderWidth || 0, v => updateEl(el.id, { borderWidth: v }))}
                        </div>
                    </div>
                )}

                {/* Text */}
                {el.type !== 'qrcode' && el.type !== 'box' && el.type !== 'image' && (
                    <div className="space-y-2">
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Testo</p>
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">Font ({unit})</p>
                            {numField('Dimensione', el.fontSize, v => updateEl(el.id, { fontSize: Math.max(1, v) }))}
                        </div>
                        {colorField('Colore', el.color, v => updateEl(el.id, { color: v }))}
                        <div>
                            <label className="text-xs text-slate-500 mb-1 block">Stile</label>
                            <select value={el.fontStyle} onChange={e => updateEl(el.id, { fontStyle: e.target.value as BadgeElement['fontStyle'] })}
                                className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-sky-500">
                                <option value="normal">Normale</option>
                                <option value="bold">Grassetto</option>
                                <option value="italic">Corsivo</option>
                                <option value="bold italic">Grassetto + Corsivo</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-500 mb-1 block">Allineamento</label>
                            <div className="flex gap-1">
                                {(['left','center','right'] as const).map(a => (
                                    <button key={a} onClick={() => updateEl(el.id, { align: a })}
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${el.align === a ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40' : 'bg-slate-800 text-slate-500 hover:text-white border border-slate-700'}`}>
                                        {a === 'left' ? '⬅' : a === 'center' ? '↔' : '➡'}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {el.type === 'custom_text' && (
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Contenuto</label>
                                <textarea value={el.text || ''} onChange={e => updateEl(el.id, { text: e.target.value })} rows={3}
                                    className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-sky-500 resize-none"/>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    /* ── render ── */
    return (
        <div className="h-full flex flex-col animate-fade-in -m-4 sm:-m-8">

            {/* Top bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-900/80 border-b border-slate-700/50 backdrop-blur flex-shrink-0">
                <button onClick={() => navigate(`${base}/badges`)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                </button>
                <input value={template.name} onChange={e => setTemplate(p => ({ ...p, name: e.target.value }))}
                    className="bg-transparent text-white font-bold text-lg focus:outline-none border-b border-transparent focus:border-sky-500 px-1 transition-colors flex-1 min-w-0"/>

                {/* Unit toggle */}
                <div className="flex bg-slate-800 rounded-lg p-0.5 border border-slate-700 flex-shrink-0">
                    {(['px','cm'] as const).map(u => (
                        <button key={u} onClick={() => setUnit(u)}
                            className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${unit === u ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                            {u}
                        </button>
                    ))}
                </div>

                {/* Grid toggle */}
                <button onClick={() => setShowGrid(g => !g)} title="Mostra/nascondi griglia"
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${showGrid ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>
                </button>

                <button onClick={() => setShowSizeModal(true)} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs transition-colors flex-shrink-0">
                    {badgeCmW}×{badgeCmH} cm
                </button>

                <button onClick={handleSave}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl font-semibold text-sm transition-all shadow-lg flex-shrink-0 ${saved ? 'bg-emerald-600 text-white' : 'bg-amber-500 hover:bg-amber-400 text-white shadow-amber-500/20'}`}>
                    {saved
                        ? <><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Salvato!</>
                        : <><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>Salva</>
                    }
                </button>
            </div>

            {/* Grid options bar (when grid is on) */}
            {showGrid && (
                <div className="flex items-center gap-3 px-4 py-2 bg-indigo-900/20 border-b border-indigo-700/30 flex-shrink-0 text-xs">
                    <span className="text-indigo-300 font-medium">Griglia</span>
                    <select value={gridSizeCm} onChange={e => setGridSizeCm(parseFloat(e.target.value))}
                        className="bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-indigo-500 text-xs">
                        {GRID_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                    <label className="flex items-center gap-1.5 text-slate-400 cursor-pointer select-none">
                        <input type="checkbox" checked={gridSnap} onChange={e => setGridSnap(e.target.checked)} className="rounded text-indigo-500 bg-slate-800 border-slate-600"/>
                        Snap alla griglia
                    </label>
                </div>
            )}

            {/* Side tabs */}
            <div className="flex border-b border-slate-700/50 bg-slate-800/30 px-4 flex-shrink-0">
                {(['front','back'] as const).map(s => (
                    <button key={s} onClick={() => { setActiveSide(s); setSelectedId(null); }}
                        className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeSide === s ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>
                        {s === 'front' ? 'Fronte' : 'Retro'}
                    </button>
                ))}
            </div>

            {/* Main */}
            <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">

                {/* Left panel */}
                <div className="w-full lg:w-56 bg-slate-900/60 border-b lg:border-b-0 lg:border-r border-slate-700/50 flex-shrink-0 overflow-y-auto p-3 space-y-4">
                    <div>
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2 px-1">Aggiungi Elemento</p>
                        <div className="space-y-1">
                            {(Object.keys(ELEMENT_LABELS) as BadgeElementType[]).filter(t => t !== 'image').map(type => (
                                <button key={type} onClick={() => addElement(type)}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 hover:bg-slate-700 text-slate-300 hover:text-white text-sm transition-colors text-left">
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ELEMENT_DOT[type]}`}/>{ELEMENT_LABELS[type]}
                                </button>
                            ))}
                            <button onClick={handleAddImage}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 hover:bg-slate-700 text-slate-300 hover:text-white text-sm transition-colors text-left">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ELEMENT_DOT.image}`}/>{ELEMENT_LABELS.image}
                            </button>
                        </div>
                    </div>

                    {/* Client ID (admin only) */}
                    {isAdmin && (
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2 px-1">Client ID</p>
                            <input
                                type="number"
                                value={clientId}
                                onChange={e => setClientId(e.target.value === '' ? '' : Number(e.target.value))}
                                placeholder="es. 5"
                                className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500"
                            />
                        </div>
                    )}

                    {/* Canvas bg */}
                    <div>
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2 px-1">Sfondo Canvas</p>
                        <div className="flex gap-2 items-center px-1">
                            <input type="color" value={currentSide.bgColor || '#1e293b'} onChange={e => updateSide(activeSide, { bgColor: e.target.value })} className="w-10 h-8 rounded border border-slate-700 bg-slate-800 cursor-pointer p-0.5"/>
                            <input type="text"  value={currentSide.bgColor || '#1e293b'} onChange={e => updateSide(activeSide, { bgColor: e.target.value })} className="flex-1 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500 font-mono"/>
                        </div>
                    </div>

                    {/* Element list */}
                    {currentSide.elements.length > 0 && (
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2 px-1">Livelli ({currentSide.elements.length})</p>
                            <div className="space-y-1">
                                {[...currentSide.elements].reverse().map((el, ri) => {
                                    const realIdx = currentSide.elements.length - 1 - ri;
                                    return (
                                        <div key={el.id} onClick={() => setSelectedId(el.id)}
                                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${selectedId === el.id ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30' : 'bg-slate-800/40 text-slate-400 hover:bg-slate-700/50 hover:text-white'}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ELEMENT_DOT[el.type]}`}/>
                                            <span className="truncate flex-1">{ELEMENT_LABELS[el.type]}</span>
                                            <div className="flex gap-0.5 ml-auto">
                                                <button onClick={e => { e.stopPropagation(); moveEl(el.id, 'up'); }} disabled={realIdx === currentSide.elements.length - 1} className="p-0.5 text-slate-600 hover:text-white disabled:opacity-30 transition-colors" title="In primo piano">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="m18 15-6-6-6 6"/></svg>
                                                </button>
                                                <button onClick={e => { e.stopPropagation(); moveEl(el.id, 'down'); }} disabled={realIdx === 0} className="p-0.5 text-slate-600 hover:text-white disabled:opacity-30 transition-colors" title="Dietro">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="m6 9 6 6 6-6"/></svg>
                                                </button>
                                                <button onClick={e => { e.stopPropagation(); deleteEl(el.id); }} className="p-0.5 text-slate-600 hover:text-red-400 transition-colors" title="Elimina">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Canvas */}
                <div className="flex-1 flex flex-col items-center justify-center overflow-auto p-6 bg-slate-950/30 min-h-0">
                    <div style={{ width: CANVAS_DISPLAY_W, height: canvasH, position: 'relative', overflow: 'hidden', backgroundColor: currentSide.bgColor || '#1e293b' }}
                        className="rounded-xl shadow-2xl ring-1 ring-slate-700/50 select-none"
                        onClick={() => setSelectedId(null)}>
                        {currentSide.elements.map(el => renderEl(el))}
                        <GridOverlay/>
                    </div>
                    <p className="text-center text-slate-600 text-xs mt-2">
                        {template.width}×{template.height} px &nbsp;·&nbsp; {badgeCmW}×{badgeCmH} cm &nbsp;·&nbsp; {DPI} DPI &nbsp;·&nbsp; scala 1:{Math.round(1/scale)}
                    </p>
                </div>

                {/* Properties */}
                <div className="w-full lg:w-64 bg-slate-900/60 border-t lg:border-t-0 lg:border-l border-slate-700/50 flex-shrink-0 overflow-y-auto p-3">
                    <PropsPanel/>
                </div>
            </div>

            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFileChange}/>

            {/* Size modal */}
            {showSizeModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6">
                        <h3 className="text-lg font-bold text-white mb-4">Dimensioni & Risoluzione</h3>
                        <div className="space-y-2 mb-4">
                            {PRESET_SIZES.map(p => {
                                const cmW = (p.width  / ((template.dpi||300)/2.54)).toFixed(1);
                                const cmH = (p.height / ((template.dpi||300)/2.54)).toFixed(1);
                                return (
                                    <button key={p.label} onClick={() => { setTemplate(prev => ({ ...prev, width: p.width, height: p.height })); setShowSizeModal(false); }}
                                        className={`w-full text-left px-4 py-3 rounded-xl border transition-colors text-sm ${template.width === p.width && template.height === p.height ? 'border-amber-500 bg-amber-500/10 text-amber-300' : 'border-slate-700 bg-slate-700/30 text-slate-300 hover:border-slate-500'}`}>
                                        <span className="font-medium">{p.label}</span>
                                        <span className="text-slate-500 ml-2">{p.width}×{p.height}px · {cmW}×{cmH}cm @ {template.dpi||300}dpi</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Larghezza (px)</label>
                                <input type="number" value={template.width} onChange={e => setTemplate(p => ({ ...p, width: Number(e.target.value) }))} className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-amber-500"/>
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Altezza (px)</label>
                                <input type="number" value={template.height} onChange={e => setTemplate(p => ({ ...p, height: Number(e.target.value) }))} className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-amber-500"/>
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">DPI</label>
                                <input type="number" value={template.dpi || 300} onChange={e => setTemplate(p => ({ ...p, dpi: Number(e.target.value) }))} className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-amber-500"/>
                            </div>
                        </div>
                        <div className="bg-slate-900/50 rounded-xl p-3 text-xs text-slate-400 mb-4">
                            Dimensione fisica: <span className="text-white font-mono">{(template.width/((template.dpi||300)/2.54)).toFixed(1)} × {(template.height/((template.dpi||300)/2.54)).toFixed(1)} cm</span>
                        </div>
                        <div className="flex justify-end">
                            <button onClick={() => setShowSizeModal(false)} className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold transition-colors">Conferma</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BadgeDesignerPage;
